<template>
  <div ref="rootRef" class="ui-select" :class="attrs.class" :style="attrs.style as StyleValue">
    <button
      v-bind="buttonAttrs"
      :id="triggerId"
      ref="triggerRef"
      type="button"
      class="ui-select__trigger"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-activedescendant="isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined"
      :disabled="disabled"
      @click="onTriggerClick"
      @keydown="onKeydown"
    >
      <span class="ui-select__value" :class="{ 'is-placeholder': !selectedOption }">
        {{ selectedOption ? selectedOption.text : placeholder }}
      </span>

      <ChevronDown class="ui-select__chevron" :size="18" :stroke-width="2.4" aria-hidden="true" />
    </button>

    <Teleport to="body">
      <Transition name="ui-pop">
        <ul
          v-if="isOpen"
          :id="listboxId"
          ref="panelRef"
          class="ui-select__list"
          role="listbox"
          :aria-labelledby="triggerId"
          :style="panelStyle"
          @mousedown="onPanelMouseDown"
        >
          <li v-if="isSearchEnabled" class="ui-select__search" role="presentation">
            <Search :size="16" :stroke-width="2.2" aria-hidden="true" />

            <input
              ref="searchRef"
              v-model="query"
              type="text"
              class="ui-select__search-input"
              :placeholder="$t('Search')"
              :aria-label="$t('Search')"
              @keydown="onKeydown"
            />
          </li>

          <template v-for="(option, index) in visibleOptions" :key="String(option.value)">
            <li v-if="option.isGroupHeader" class="ui-select__group" role="presentation">
              {{ option.text }}
            </li>

            <li
              v-else
              :id="optionId(index)"
              class="ui-select__option"
              :class="{
                'is-selected': option.value === modelValue,
                'is-active': index === activeIndex,
                'is-disabled': option.disabled,
              }"
              role="option"
              :aria-selected="option.value === modelValue"
              :aria-disabled="option.disabled || undefined"
              @click="select(option)"
              @mousemove="onOptionHover(option, index)"
            >
              <UiSelectCheck :checked="option.value === modelValue" />

              <span class="ui-select__text">{{ option.text }}</span>
            </li>
          </template>

          <li v-if="visibleOptions.length === 0" class="ui-select__empty" role="presentation">
            {{ $t("No options found") }}
          </li>
        </ul>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useAttrs, useId, watch } from "vue";
import type { StyleValue } from "vue";
import { useAnchoredPopover } from "@/components/newUi/core/useAnchoredPopover";
import {
  isSelectableOption,
  useSelectList,
  type SelectListOption,
} from "@/components/newUi/forms/useSelectList";
import { ChevronDown, Search } from "lucide-vue-next";
import UiSelectCheck from "@/components/newUi/forms/UiSelectCheck.vue";

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null;
    options: SelectListOption[];
    placeholder?: string;
    disabled?: boolean;
    nullAsPlaceholder?: boolean;
    searchable?: boolean;
  }>(),
  {
    modelValue: null,
    placeholder: "",
    disabled: false,
    nullAsPlaceholder: false,
    searchable: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: string | number | null];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const baseId = useId();
const listboxId = `ui-select-list-${baseId}`;
const triggerId = computed(() => (attrs.id as string | undefined) ?? `ui-select-${baseId}`);
const buttonAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== "class" && key !== "style")),
);
const {
  rootRef,
  triggerRef,
  panelRef,
  isOpen,
  panelStyle,
  open: openPopover,
  close,
} = useAnchoredPopover({ matchWidth: true });
const searchRef = ref<HTMLInputElement | null>(null);
const activeIndex = ref(0);

const { query, isSearchEnabled, visibleOptions, firstSelectableIndex, stepSelectableIndex } =
  useSelectList(
    () => props.options,
    () => props.searchable,
  );

const selectedOption = computed(() => {
  if (props.nullAsPlaceholder && props.modelValue === null) {
    return null;
  }

  return (
    props.options.find((option) => !option.isGroupHeader && option.value === props.modelValue) ??
    null
  );
});

const optionId = (index: number): string => `${listboxId}-option-${index}`;

const scrollActiveIntoView = (): void => {
  panelRef.value?.querySelector(".is-active")?.scrollIntoView?.({ block: "nearest" });
};

const open = async (): Promise<void> => {
  if (props.disabled || isOpen.value) {
    return;
  }

  query.value = "";

  const selectedIndex = visibleOptions.value.findIndex(
    (option) => !option.isGroupHeader && option.value === props.modelValue,
  );

  activeIndex.value = selectedIndex >= 0 ? selectedIndex : firstSelectableIndex();

  openPopover();

  await nextTick();

  scrollActiveIntoView();

  searchRef.value?.focus();
};

const onTriggerClick = (): void => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const select = (option: SelectListOption): void => {
  if (!isSelectableOption(option)) {
    return;
  }

  emit("update:modelValue", option.value);
  close();
  triggerRef.value?.focus();
};

const onPanelMouseDown = (event: MouseEvent): void => {
  if (!(event.target as HTMLElement).closest(".ui-select__search-input")) {
    event.preventDefault();
  }
};

const onOptionHover = (option: SelectListOption, index: number): void => {
  if (isSelectableOption(option)) {
    activeIndex.value = index;
  }
};

const move = (delta: number): void => {
  activeIndex.value = stepSelectableIndex(activeIndex.value, delta);

  nextTick(scrollActiveIntoView);
};

const onKeydown = (event: KeyboardEvent): void => {
  const isSearchInput = (event.target as HTMLElement).classList.contains("ui-select__search-input");

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();

      if (isOpen.value) {
        move(1);
      } else {
        open();
      }
      break;
    case "ArrowUp":
      event.preventDefault();

      if (isOpen.value) {
        move(-1);
      } else {
        open();
      }
      break;
    case "Home":
      if (isOpen.value && !isSearchInput) {
        event.preventDefault();
        activeIndex.value = firstSelectableIndex();
      }
      break;
    case "End":
      if (isOpen.value && !isSearchInput) {
        event.preventDefault();
        activeIndex.value = stepSelectableIndex(visibleOptions.value.length, -1);
      }
      break;
    case "Enter":
    case " ":
      if (event.key === " " && isSearchInput) {
        break;
      }

      event.preventDefault();

      if (isOpen.value) {
        const option = visibleOptions.value[activeIndex.value];

        if (option) {
          select(option);
        }
      } else {
        open();
      }
      break;
    case "Escape":
      if (isOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        close();
        triggerRef.value?.focus();
      }
      break;
    case "Tab":
      close();
      break;
  }
};

watch(query, () => {
  activeIndex.value = firstSelectableIndex();
});

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      close();
    }
  },
);
</script>
